import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import Panel, { PanelBody } from '@aurora/shared-client/components/common/Panel/Panel';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import IntlWrapperContext from '@aurora/shared-client/components/context/IntlContext/IntlWrapperContext';
import useLocale from '@aurora/shared-client/components/useLocale';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useContext, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import useTranslation from '../../useTranslation';
import type { TermsOfServiceType } from '../TermsOfServiceHelper';
import { useQueryTermsOfServiceForLanguageWithTracing } from '../TermsOfServiceHelper';
import localStyles from './TermsOfServiceWidget.module.pcss';

const LanguageSelectionModal = dynamic(
  () => import('../TermsOfServiceActionMenu/LanguageSelectionModal/LanguageSelectionModal'),
  {
    ssr: false
  }
);
const TermsOfServiceEditModal = dynamic(
  () => import('../TermsOfServiceEditModal/TermsOfServiceEditModal'),
  {
    ssr: false
  }
);

interface Props extends WidgetProps {
  /**
   * The terms of service type
   */
  termsOfServiceType: TermsOfServiceType;
}

/**
 * TermsOfService Widget Component used for TermsOfService, CommunityGuideLine, PrivacyPolicy page.
 *
 * @author Dolan Halbrook, Amit Agrawal, Sureshkumar M
 */
const TermsOfServiceWidget: WidgetFC<Props> = ({ termsOfServiceType }) => {
  const cx = useClassNameMapper(localStyles);
  const { loading: textLoading, formatMessage } = useTranslation(
    EndUserComponent.TERMS_OF_SERVICE_WIDGET
  );

  const [showLanguageSelectionModal, setShowLanguageSelectionModal] = useState<boolean>(false);
  const [showTermsOfServiceEditModal, setShowTermsOfServiceEditModal] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  const {
    community: {
      userContext: { canUpdateNode }
    }
  } = useContext(AppContext);

  const { allowedLanguages } = useContext(IntlWrapperContext);

  const locale = useLocale();

  const { data: termsOfServiceData, loading: termsOfServiceLoading } =
    useQueryTermsOfServiceForLanguageWithTracing(termsOfServiceType, locale, true);

  if (textLoading || termsOfServiceLoading) {
    return null;
  }

  const languageSelectionEnabled: boolean = allowedLanguages.length > 1;

  const content = termsOfServiceData?.[termsOfServiceType]?.content;

  /**
   * Renders the language selection modal
   * @returns React Element
   */
  function renderLanguageSelectionModal(): React.ReactElement {
    return (
      <LanguageSelectionModal
        show={showLanguageSelectionModal}
        termsOfServiceType={termsOfServiceType}
        onHide={() => setShowLanguageSelectionModal(false)}
        onSubmit={language => {
          setSelectedLanguage(language);
          setShowTermsOfServiceEditModal(true);
          setShowLanguageSelectionModal(false);
        }}
      />
    );
  }

  /**
   * Renders the term of service edit modal
   * @returns React Element
   */
  function renderTermsOfServiceEditModal(): React.ReactElement {
    return (
      <TermsOfServiceEditModal
        show={showTermsOfServiceEditModal}
        onHide={() => setShowTermsOfServiceEditModal(false)}
        languageCode={selectedLanguage}
        termsOfServiceType={termsOfServiceType}
      />
    );
  }

  /**
   * Renders the widget empty state
   * @returns React Element
   */
  function renderEmptyState(): React.ReactElement {
    return (
      <EmptyState title={formatMessage('termsOfService.emptyState')}>
        {canUpdateNode && (
          <Button
            variant={ButtonVariant.LINK}
            onClick={() => {
              if (languageSelectionEnabled) {
                setShowLanguageSelectionModal(true);
              } else {
                setSelectedLanguage(allowedLanguages[0]);
                setShowTermsOfServiceEditModal(true);
              }
            }}
          >
            {formatMessage('editTermsOfService', { termsOfServiceType })}
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <>
      <main className={cx('lia-terms-of-service-container', { 'lia-g-mt-50': !content })}>
        <Panel>
          <PanelBody className={cx({ 'lia-g-p-0': !content })}>
            {content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : renderEmptyState()}
          </PanelBody>
        </Panel>
      </main>
      {showLanguageSelectionModal && renderLanguageSelectionModal()}
      {showTermsOfServiceEditModal && renderTermsOfServiceEditModal()}
    </>
  );
};

export default TermsOfServiceWidget;
